import {
  getStoredAnonymousCartId,
  removeStoredAnonymousCartId,
  setStoredAnonymousCartId,
} from '@unity/commerce-core';
import { subscribeToCartChanged } from './cartChanged';

/**
 * The guest cart (CPS-7693). commerce-core 0.24 owns it (ILT-24593): with
 * `enableAnonymousCart` its provider creates the cart, stores its id per
 * store in localStorage and reads it back on the next visit. This module only
 * names that record for the Compass store, so the token layer can carry the
 * cart into the user's on sign-in and forget it once nothing can read it.
 */
const GUEST_CART_STORE = 'asset';

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** The guest cart this browser holds, if any. */
export const readGuestCartId = (): string | undefined =>
  getStoredAnonymousCartId(GUEST_CART_STORE);

/** Follows the cart commerce-core holds when an update replaces it. */
export const rememberGuestCart = (cartId: string): void => {
  if (readGuestCartId() === cartId) return;
  setStoredAnonymousCartId(GUEST_CART_STORE, cartId);
  notify();
};

/**
 * Drops the guest cart: merged into the user's cart, gone, or held by a guest
 * identity that has lapsed. Given `cartId`, only that cart is dropped, so a
 * newer one written meanwhile survives.
 */
export const forgetGuestCart = (cartId?: string): void => {
  const stored = readGuestCartId();
  if (!stored || (cartId && stored !== cartId)) return;
  removeStoredAnonymousCartId(GUEST_CART_STORE);
  notify();
};

/**
 * Changes to the guest cart record: ours, another tab's (`storage`), and a
 * cart change in this tab, after which commerce-core may have created one.
 */
export const subscribeToGuestCart = (listener: () => void): (() => void) => {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  const unsubscribeCartChanged = subscribeToCartChanged(listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
    unsubscribeCartChanged();
  };
};
